import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CATEGORIA_META, ORDEM_CATEGORIA, celulasDoMes, mesCurto, nomeDoMes, outroMes, type EventoAgenda } from "../lib/perito/agenda";
import { diasAte, formatarData, hoje } from "../lib/perito/datas";
import { rotuloDias, tomDosDias } from "../lib/perito/proximaAcao";
import type { Pericia } from "../lib/perito/tipos";
import { BOTAO_SECUNDARIO, Chip } from "./PeritoUi";

// A agenda da Central do Perito (fase B), portada de `src/components/prazos/CalendarioMensal.tsx` e da lista de
// `src/routes/_authenticated/prazos.tsx`. Os dias chegam como texto (AAAA-MM-DD) de `lib/perito/agenda.ts`, e
// nenhum `Date` entra no meio: lá o calendário montava as células com o relógio do navegador e lia cada data
// como meia-noite UTC.

const DIAS_DA_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** O dia como bloco ("06 · NOV"): é a informação que o olho procura primeiro numa lista de compromissos. */
export function DiaEmBloco({ dia }: { dia: string }) {
  return (
    <span className="grid w-12 shrink-0 place-items-center rounded-xl bg-chip py-1.5 text-center" aria-hidden="true">
      <span className="block font-display text-lg leading-none font-bold text-titulo tabular-nums">{dia.slice(8, 10)}</span>
      <span className="mt-0.5 block text-[11px] font-semibold text-chip-tinta uppercase">{mesCurto(dia)}</span>
    </span>
  );
}

export function LegendaDaAgenda() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-legenda-da-agenda>
      {ORDEM_CATEGORIA.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5 text-xs font-semibold text-cinza">
          <span aria-hidden="true" className={`size-2.5 rounded-full ${CATEGORIA_META[c].ponto}`} />
          {CATEGORIA_META[c].rotulo}
        </span>
      ))}
    </div>
  );
}

/** O nome curto da perícia, para a linha da agenda: o apelido, senão o número do processo. */
function nomeCurto(p: Pericia): string {
  return p.apelido?.trim() || p.numeroProcesso || "Perícia sem número";
}

/** Uma linha da agenda: o dia, o que é, a categoria, a perícia e quanto falta. */
function LinhaDoEvento({ raiz, e, pericia, aoEditarPrazo }: { raiz: string; e: EventoAgenda; pericia?: Pericia; aoEditarPrazo?: (id: string) => void }) {
  const meta = CATEGORIA_META[e.categoria];
  const dias = diasAte(e.dia);
  const idDoPrazo = e.id.startsWith("prazo:") && !e.id.includes(":antes-") ? e.id.slice("prazo:".length) : null;
  return (
    <li className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-evento={e.id} data-categoria={e.categoria} data-dia={e.dia}>
      <DiaEmBloco dia={e.dia} />
      <span className="sr-only">{formatarData(e.dia)}</span>
      <div className="min-w-[12rem] flex-1">
        <p className={`font-semibold ${e.concluido ? "text-cinza line-through" : "text-titulo"}`}>{e.titulo}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-cinza">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-semibold ${meta.selo}`}>
            <span aria-hidden="true" className={`size-1.5 rounded-full ${meta.ponto}`} /> {meta.rotulo}
          </span>
          {pericia && <Link to={`${raiz}/pericias/${pericia.id}`} className="font-semibold text-link hover:underline">{nomeCurto(pericia)}</Link>}
        </div>
      </div>
      {/* O lembrete já diz no título quanto falta para o PRAZO; um selo com os dias até o próprio lembrete seria um
          segundo número na mesma linha ("Faltam 3 dias" ao lado de "2 dias", a foto achou). */}
      {e.concluido ? <Chip tom="ok">Concluído</Chip> : e.categoria !== "lembrete" && <Chip tom={tomDosDias(dias)}>{rotuloDias(dias)}</Chip>}
      {idDoPrazo && aoEditarPrazo && (
        <button type="button" onClick={() => aoEditarPrazo(idDoPrazo)} className="text-xs font-semibold text-link hover:underline" aria-label={`Editar o prazo ${e.titulo}`}>
          Editar
        </button>
      )}
    </li>
  );
}

/** A agenda em lista, para as janelas de hoje, 7 e 30 dias. */
export function ListaDaAgenda({ raiz, eventos, pericias, aoEditarPrazo, vazio }: {
  raiz: string; eventos: EventoAgenda[]; pericias: Pericia[]; aoEditarPrazo?: (id: string) => void; vazio: string;
}) {
  const porId = useMemo(() => new Map(pericias.map((p) => [p.id, p])), [pericias]);
  return (
    <div className="surface overflow-hidden" data-lista-da-agenda>
      {eventos.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-cinza">{vazio}</p>
      ) : (
        <ul className="divide-y divide-borda">
          {eventos.map((e) => <LinhaDoEvento key={e.id} raiz={raiz} e={e} pericia={e.pericia_id ? porId.get(e.pericia_id) : undefined} aoEditarPrazo={aoEditarPrazo} />)}
        </ul>
      )}
      <div className="border-t border-borda px-5 py-3"><LegendaDaAgenda /></div>
    </div>
  );
}

/**
 * O mês em grade. Hoje tem o círculo laranja da marca; o dia escolhido, o contorno do acento. No celular a célula
 * mostra só os PONTOS das categorias (sete colunas em 390px não cabem título nenhum), e a lista do dia, embaixo,
 * diz o que é cada um. O mês e o dia escolhido são de quem chama, que os guarda no endereço.
 */
export function CalendarioMensal({ raiz, eventos, pericias, mes, aoMudarMes, dia, aoEscolherDia, aoEditarPrazo }: {
  raiz: string; eventos: EventoAgenda[]; pericias: Pericia[]; mes: string; aoMudarMes: (mes: string) => void;
  dia: string; aoEscolherDia: (dia: string) => void; aoEditarPrazo?: (id: string) => void;
}) {
  const hojeSP = hoje();
  const porDia = useMemo(() => {
    const m = new Map<string, EventoAgenda[]>();
    for (const e of eventos) m.set(e.dia, [...(m.get(e.dia) ?? []), e]);
    return m;
  }, [eventos]);
  const porId = useMemo(() => new Map(pericias.map((p) => [p.id, p])), [pericias]);
  const celulas = celulasDoMes(mes);
  const doDia = porDia.get(dia) ?? [];
  const contagem = (n: number) => `${n} ${n === 1 ? "compromisso" : "compromissos"}`;

  return (
    <div className="surface overflow-hidden" data-calendario data-mes={mes}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda px-5 py-4">
        <h2 className="font-display text-lg font-bold text-titulo first-letter:uppercase" data-nome-do-mes>{nomeDoMes(mes)}</h2>
        <div className="flex items-center gap-1">
          <button type="button" className={BOTAO_SECUNDARIO} onClick={() => { aoMudarMes(hojeSP.slice(0, 7)); aoEscolherDia(hojeSP); }} data-mes-de-hoje>Hoje</button>
          <button type="button" onClick={() => aoMudarMes(outroMes(mes, -1))} aria-label="Mês anterior" className="grid size-10 place-items-center rounded-full text-cinza hover:bg-chip hover:text-titulo" data-mes-anterior>‹</button>
          <button type="button" onClick={() => aoMudarMes(outroMes(mes, 1))} aria-label="Próximo mês" className="grid size-10 place-items-center rounded-full text-cinza hover:bg-chip hover:text-titulo" data-mes-seguinte>›</button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-borda bg-fundo" aria-hidden="true">
        {DIAS_DA_SEMANA.map((d) => <div key={d} className="px-1 py-2 text-center text-[11px] font-bold tracking-[0.12em] text-cinza uppercase">{d}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {celulas.map((c, i) => {
          if (!c) return <div key={`v${i}`} className="min-h-16 border-r border-b border-borda bg-fundo sm:min-h-24" />;
          const lista = porDia.get(c) ?? [];
          const ehHoje = c === hojeSP;
          const escolhido = c === dia;
          return (
            <button key={c} type="button" onClick={() => aoEscolherDia(c)} aria-pressed={escolhido}
              aria-label={`${formatarData(c)}: ${contagem(lista.length)}`}
              className={`min-h-16 border-r border-b border-borda p-1.5 text-left align-top transition-colors sm:min-h-24 ${escolhido ? "ring-2 ring-acento ring-inset" : "hover:bg-chip"}`}
              data-dia={c} data-quantos={lista.length}>
              <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-bold tabular-nums ${ehHoje ? "bg-laranja text-azul" : "text-tinta"}`}>{Number(c.slice(8, 10))}</span>
              <span className="mt-1 flex flex-wrap gap-1 sm:hidden">
                {lista.slice(0, 4).map((e) => <span key={e.id} aria-hidden="true" className={`size-1.5 rounded-full ${CATEGORIA_META[e.categoria].ponto}`} />)}
              </span>
              <span className="mt-1 hidden flex-col gap-0.5 sm:flex">
                {lista.slice(0, 3).map((e) => (
                  // O concluído vira o selo NEUTRO riscado: esmaecer com opacidade derrubava o texto (lá, 2,93 : 1).
                  <span key={e.id} className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 ${e.concluido ? "bg-chip text-chip-tinta" : CATEGORIA_META[e.categoria].selo}`}>
                    <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${CATEGORIA_META[e.categoria].ponto}`} />
                    <span className={`truncate text-[11px] font-medium ${e.concluido ? "line-through" : ""}`}>{e.titulo}</span>
                  </span>
                ))}
                {lista.length > 3 && <span className="pl-1 text-[11px] font-semibold text-cinza">+{contagem(lista.length - 3)}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="border-b border-borda px-5 py-3"><LegendaDaAgenda /></div>

      <div className="px-5 py-4" data-dia-escolhido={dia}>
        <h3 className="mb-3 text-[11px] font-bold tracking-[0.14em] text-cinza uppercase">{formatarData(dia)} · {contagem(doDia.length)}</h3>
        {doDia.length === 0 ? (
          <p className="text-sm text-cinza">Nenhum compromisso neste dia.</p>
        ) : (
          <ul className="-mx-5 divide-y divide-borda border-y border-borda">
            {doDia.map((e) => <LinhaDoEvento key={e.id} raiz={raiz} e={e} pericia={e.pericia_id ? porId.get(e.pericia_id) : undefined} aoEditarPrazo={aoEditarPrazo} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
